import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { AutocompleteAsyncField, InputField } from "@/features/FormCard/Fields";

import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";

import { FormActionsNew } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";
import { fieldTranslations } from "./translations";

const FormSchema = z.object({
  structured_product_id: z.coerce.number().nullable().optional(),
  structured_product_name: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      // we recv / send as string the data
      z.string().optional().nullable(),
      // for the autocomplete, the form state is an object
      z.object({
        id: z.number(),
        text: z.string(),
      }),
    ]),
  ),
  underlying_id: z.coerce.number().nullable().optional(),
  underlying_name: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      // we recv / send as string the data
      z.string().optional().nullable(),
      // for the autocomplete, the form state is an object
      z.object({
        id: z.number(),
        text: z.string(),
      }),
    ]),
  ),
  weight: z.coerce.number().nullable().optional(),
  initial_strike_price: z.coerce.number().nullable().optional(),
  a5_time_serie_file_id: z.coerce.number().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

const client = "a5";
const table_name = "a5_derivative_underlyings";

export function A5DerivativeUnderlyingsNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
  });

  const navigateToList = () => {
    navigate(-1);
  };

  const structured_product_id = form.watch("structured_product_name.id");
  const underlying_id = form.watch("underlying_name.id");
  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("structured_products.new_basket_underlying", {
                  defaultValue: "New structured product basket underlying",
                })}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <AutocompleteAsyncField
                  name="structured_product_name"
                  label={t(`${table_name}.columns.structured_product_name`, {
                    ns: "tables",
                    defaultValue: "Produit structuré",
                  })}
                  isRequired={true}
                  control={form.control}
                  base_url="a5/a5_static_data"
                  id_column="structured_product_id"
                  column_name="name"
                  options={{
                    translateKey: "asset_class_2",
                    client: "a5",
                    table_name: "a5_static_data",
                    language: t("language.code", { defaultValue: "en" }),
                    itemLink: `/admin/a5/a5_static_data/${structured_product_id}`,
                    filters: () => {
                      const equals = { asset_class_2: "structured_product" };
                      return { equals };
                    },
                  }}
                />
                <AutocompleteAsyncField
                  name="underlying_name"
                  label={t(`${table_name}.columns.underlying_name`, {
                    ns: "tables",
                    defaultValue: "Sous-jacent",
                  })}
                  isRequired={true}
                  control={form.control}
                  base_url="a5/a5_static_data"
                  id_column="underlying_id"
                  column_name="name"
                  options={{
                    translateKey: "asset_class_2",
                    client: "a5",
                    table_name: "a5_static_data",
                    language: t("language.code", { defaultValue: "en" }),
                    itemLink: `/admin/a5/a5_static_data/${underlying_id}`,
                  }}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="weight"
                    label={t(`${table_name}.columns.weight`, {
                      ns: "tables",
                      defaultValue: "Pondération",
                    })}
                    control={form.control}
                    type="number"
                  />
                  <InputField
                    name="initial_strike_price"
                    label={t(`${table_name}.columns.initial_strike_price`, {
                      ns: "tables",
                      defaultValue: "Prix de strike initial",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
          children={
            changedFields
              ? NewDiff(
                  changedFields,
                  ["structured_product_id", "underlying_id"],
                  fieldTranslations,
                  table_name,
                )
              : t("no_changes")
          }
        />
        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
